import "./messagingHealth.scss";
import { Ring } from "@uiball/loaders";
import FieldHelp from "components/Ui/FieldHelp/FieldHelp";
import { BRAND } from "constants/brand";
import { useGetMessagingHealth } from "hooks/useMessagingHealth";
import { WHATSAPP_INSTANCE_LABELS, formatWhatsappInstancePhone } from "constants/whatsappInstances";

/**
 * Outbound messaging status, on its dedicated dashboard page.
 *
 * WHY IT EXISTS: on 2026-08-03 both WhatsApp instances sat unlinked for days,
 * silently queueing every order receipt, status update and verification code.
 * Nothing showed it — the UltraMsg dashboard said "Active" because the
 * subscription was paid, and the API reported success because UltraMsg answers
 * a queue receipt the same way it answers a real send. It was found only when a
 * developer noticed his test messages never arrived.
 *
 * So this answers one question at a glance: **are messages actually going out
 * right now?** The API polls the channels every five minutes and alerts on
 * changes; this is the place to look on purpose.
 */

/**
 * What each UltraMsg auth status means, in words an operator can act on.
 *
 * Only "authenticated" actually delivers. Every other value means messages are
 * being accepted into a queue that nothing is draining — which is the whole
 * trap this card exists to expose, and the reason each one carries an
 * explanation rather than just a label nobody can interpret.
 */
const WHATSAPP_STATUS_META = {
    authenticated: {
        label: "متصل",
        help: "الرقم مرتبط بواتساب والرسائل تُرسل بشكل طبيعي.",
    },
    qr: {
        label: "بانتظار مسح رمز QR",
        help: "الرقم غير مرتبط. افتح لوحة UltraMsg وامسح رمز QR من واتساب على الهاتف. ⚠️ امسح قائمة الانتظار أولاً، وإلا سيتم إرسال كل الرسائل القديمة دفعة واحدة.",
    },
    initialize: {
        label: "قيد التشغيل",
        help: "الجلسة تُقلع الآن بعد المسح أو إعادة التشغيل. انتظر دقيقة ثم اضغط تحديث — الرسائل لا تصل حتى تكتمل.",
    },
    loading: {
        label: "قيد التحميل",
        help: "الجلسة قيد التحميل. انتظر قليلاً ثم اضغط تحديث. الرسائل لا تصل حتى تكتمل.",
    },
    standby: {
        label: "غير متصل",
        help: "الجلسة متوقفة والرقم غير مرتبط. الرسائل تُخزن في قائمة انتظار ولا تصل لأحد — أعد الربط من لوحة UltraMsg.",
    },
    disconnected: {
        label: "منقطع",
        help: "انقطع الاتصال بالهاتف. تأكد أن هاتف الواتساب متصل بالإنترنت، ثم أعد الربط إذا لزم.",
    },
};

/**
 * "الرقم الأول — 055-2895625".
 *
 * The label comes from the shared constant so this card and the city picker
 * call the same account the same thing. The number comes from UltraMsg, live —
 * it is not stored anywhere, because the stored one had already drifted.
 */
const describeInstance = (instance) => {
    return {
        label: WHATSAPP_INSTANCE_LABELS[instance.number] ?? instance.instanceId,
        // No fallback to a stored number: UltraMsg reports one only while the
        // instance is linked, and a blank is honest where a stale value is not.
        phone: formatWhatsappInstancePhone(instance.phone),
    };
};

/**
 * Latin text inside an RTL paragraph gets reordered by the bidi algorithm —
 * "055-2895625" renders as "2895625-055", which is a different phone number as
 * far as anyone reading it is concerned. <bdi> isolates it so it reads the way
 * it was written. Every number and identifier in this card goes through it.
 */
const Ltr = ({ children }) => (
    <bdi dir="ltr" className="messaging-health__ltr">
        {children}
    </bdi>
);

/**
 * Arabic names for each kind of message the platform sends. UI copy only — the
 * API says which channels each one uses, derived from the same flags the
 * senders read, so this list can go stale in wording but never in behaviour.
 */
const MESSAGE_TYPE_LABELS = {
    otp: "رمز تسجيل الدخول — المسار الجديد",
    otp_legacy: "رمز تسجيل الدخول — التطبيقات المنشورة",
    order_created_customer: "فاتورة الطلب — للزبون (طلبات السلة فقط)",
    order_created_vendor: "فاتورة الطلب — للمطعم (طلبات السلة فقط)",
    order_status: "«قيد التحضير» و«تم الرفض» — للزبون",
    statement: "كشف الحساب — للمطعم أو السائق",
    order_available_driver: "تنبيه طلب متاح — للسائق",
    order_new_vendor: "تنبيه طلب جديد — للمطعم",
    order_update_customer: "تحديث حالة الطلب — للزبون (طلبات السلة فقط)",
    admin_broadcast: "إشعار عام — يُرسل يدوياً من الداشبورد",
};

/**
 * The conditions each message actually fires under. The labels say who and
 * roughly what; these say when, because several of these are far narrower than
 * their name suggests and the gap is where wrong expectations come from.
 */
const MESSAGE_TYPE_HELP = {
    otp: "يُرسل عند طلب رمز الدخول من المسار الجديد (/api/auth/otp/request). لا يستخدمه أي تطبيق منشور بعد.",
    otp_legacy:
        "المسار القديم (user/send-otp) يستدعي الواتساب مباشرة ولا يمر بنظام القنوات، لذا يبقى واتساب فقط مهما كانت إعدادات SMS — حتى تُحدَّث التطبيقات المنشورة.",
    order_created_customer:
        "طلبات السلة فقط. طلبات السائقين والطلب السريع والطلب الخاص لا تُرسل أي فاتورة للزبون إطلاقاً. ويمكن إيقافها لمدينة معينة من إعدادات المدينة.",
    order_created_vendor:
        "طلبات السلة فقط، ويمكن إيقافها لمدينة معينة من إعدادات المدينة. باقي أنواع الطلبات لا تُرسل فاتورة للمطعم.",
    order_status:
        "حالتان فقط: «قيد التحضير» و«تم الرفض». باقي الحالات (جاهز، في الطريق، تم التوصيل) لا تُرسل أي رسالة. ويمكن إيقافها لمدينة معينة.",
    statement: "يُرسل يدوياً عند إنشاء الكشف، ويذهب للمطعم أو للسائق حسب نوع الكشف.",
    order_available_driver:
        "ينبّه السائقين في مدينة الطلب ومنطقته فقط. تنبيه فوري بلا أي سجل داخل التطبيق — السائق الذي لا يصله الإشعار لا يجد أثراً له لاحقاً.",
    order_new_vendor: "عند وصول طلب جديد للمطعم، أو عند إعادة التنبيه يدوياً من الداشبورد.",
    order_update_customer:
        "طلبات السلة فقط ولمن لديه حساب. لا يُرسل عند الحالة «جاهز». طلبات السائقين والطلب الخاص لا تُرسل أي إشعار للزبون.",
    admin_broadcast:
        "الإشعار الذي تُرسله يدوياً من صفحة الإعدادات — للكل، أو لمدينة، أو لحساب واحد.",
};

const CHANNEL_LABELS = { whatsapp: "واتساب", sms: "SMS", push: "إشعار", inapp: "داخل التطبيق" };

const PUSH_ROLE_LABELS = {
    customer: "الزبائن",
    vendor: "المطاعم",
    delivery: "السائق",
};

/**
 * Push reach for one audience.
 *
 * The number that matters is the one WITHOUT a device: those accounts cannot be
 * pushed at all, which for drivers means the order alarm never reaches them.
 * Flagged red for vendors and drivers, where it costs orders; left plain for
 * customers, where it mostly means the app is not installed and is not a fault.
 */
/**
 * Per-audience explanation. The customer one carries a warning the other two do
 * not need: its denominator is every customer-role row, which includes
 * duplicates and accounts that never ordered, so the ratio reads far worse than
 * the reality.
 */
const PUSH_HELP = {
    default:
        "الحسابات النشطة التي لديها جهاز مسجَّل للإشعارات. الحساب بلا جهاز لا يصله أي إشعار إطلاقاً. ملاحظة: وجود جهاز مسجَّل لا يضمن الوصول، فقد يكون التسجيل قديماً بعد إعادة تثبيت التطبيق.",
    delivery:
        "السائقون النشطون الذين لديهم جهاز مسجَّل. السائق بلا جهاز لا يصله تنبيه الطلب إطلاقاً — وهذا يعني طلبات لا يراها أحد. ملاحظة: وجود جهاز مسجَّل لا يضمن الوصول، فقد يكون التسجيل قديماً.",
    vendor: "المطاعم النشطة التي لديها جهاز مسجَّل. المطعم بلا جهاز لا يصله إشعار الطلب الجديد. ملاحظة: وجود جهاز مسجَّل لا يضمن الوصول، فقد يكون التسجيل قديماً.",
    customer:
        "⚠️ هذا الرقم لا يعني عدد الزبائن الفعليين: المقام يشمل كل حساب زبون غير محظور — بما فيه الحسابات المكررة (نفس الرقم بأكثر من حساب) والحسابات التي لم تطلب أبداً. أغلب هذه الحسابات ببساطة لم يعد التطبيق مثبتاً عليها، وهذا ليس عطلاً.",
};

const PushRow = ({ entry }) => {
    const missing = Math.max(0, entry.users - entry.withDevice);
    const isCritical = missing > 0 && entry.role !== "customer";

    return (
        <div className="messaging-health__row">
            <span className="messaging-health__name">
                {PUSH_ROLE_LABELS[entry.role] ?? entry.role}
                <FieldHelp>{PUSH_HELP[entry.role] ?? PUSH_HELP.default}</FieldHelp>
            </span>
            <span className="messaging-health__value">
                <Ltr>{entry.withDevice}</Ltr>
                {" / "}
                <Ltr>{entry.users}</Ltr>
                {missing > 0 ? (
                    <span
                        className={
                            isCritical
                                ? "messaging-health__stats is-bad"
                                : "messaging-health__cities"
                        }
                    >
                        {" — بلا جهاز "}
                        <Ltr>{missing}</Ltr>
                    </span>
                ) : null}
            </span>
        </div>
    );
};

/** One row: what the message is, and which channels carry it today. */
const MessageTypeRow = ({ type }) => (
    <div className="messaging-health__row">
        <span className="messaging-health__name">
            {MESSAGE_TYPE_LABELS[type.key] ?? type.key}
            {type.note === "legacy" ? (
                <FieldHelp>
                    هذا المسار لا يمر عبر نظام القنوات الجديد، لذا يبقى واتساب فقط مهما كانت إعدادات
                    SMS — حتى تُحدَّث التطبيقات المنشورة.
                </FieldHelp>
            ) : null}
        </span>
        <span className="messaging-health__channels">
            {type.channels.map((channel) => (
                <span key={channel} className="messaging-health__channel">
                    {CHANNEL_LABELS[channel] ?? channel}
                </span>
            ))}
        </span>
    </div>
);

const StatusPill = ({ isHealthy, children }) => (
    <span className={`messaging-health__pill messaging-health__pill--${isHealthy ? "ok" : "bad"}`}>
        {isHealthy ? "✅" : "⚠️"} {children}
    </span>
);

/** "قبل ٣ دقائق" — how old the reading is, so a frozen page is obvious. */
const formatAge = (observedAt) => {
    if (!observedAt) return "";
    const seconds = Math.max(0, Math.round((Date.now() - new Date(observedAt).getTime()) / 1000));
    if (seconds < 60) return "قبل ثوانٍ";
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `قبل ${minutes} دقيقة`;
    return `قبل ${Math.round(minutes / 60)} ساعة`;
};

/** One WhatsApp instance: who it is, and whether it is currently connected. */
/**
 * "اليوم: أُرسلت 12 · لم تصل 3" — one day's movement.
 *
 * Renders the row even with nothing to show. Daily figures come from
 * differencing readings taken every five minutes, so a day has none until two
 * have been taken — which on the first day means yesterday is simply empty.
 * Hiding the row entirely made that look like a bug rather than a start-up
 * condition, so it says so instead.
 */
const DayCounters = ({ label, counts }) => (
    <span className="messaging-health__stats">
        {label}:{" "}
        {counts ? (
            <>
                أُرسلت <Ltr>{counts.sent}</Ltr>
                {" · "}
                <span className={counts.expired > 0 ? "is-bad" : undefined}>
                    لم تصل <Ltr>{counts.expired}</Ltr>
                </span>
            </>
        ) : (
            <span className="messaging-health__muted">
                لا توجد قراءات كافية بعد
                <FieldHelp>
                    الأرقام اليومية تُحسب من الفرق بين قراءتين للعدادات، والقراءة تتم كل ٥ دقائق.
                    لذلك تبقى فارغة حتى تتوفر قراءتان لليوم — ويوم أمس يبقى فارغاً حتى مرور يوم كامل
                    على تشغيل الميزة. الأرقام الإجمالية تحت غير متأثرة.
                </FieldHelp>
            </span>
        )}
    </span>
);

/**
 * Provider-observed SMS credit movement for one day.
 *
 * Falls back to the API ledger while balance snapshots are still warming up.
 * The fallback is labelled explicitly because legacy clients bypass that
 * ledger and would otherwise make a partial number look authoritative.
 */
const SmsDayRow = ({ label, day = {} }) => {
    const { consumed, coverage, sent, failed } = day;
    const isProviderObserved = consumed !== null && consumed !== undefined;
    const value = isProviderObserved ? consumed : sent;

    return (
        <div className="messaging-health__row">
            <span className="messaging-health__name">
                {label}
                <FieldHelp>
                    الاستهلاك المرصود من رصيد المزوّد، ويشمل الرسائل التي أرسلتها التطبيقات القديمة
                    مباشرة. إذا لم تتوفر قراءات كافية من المزوّد، يظهر عدد المحاولات المسجّلة من
                    السيرفر فقط. وتظهر عبارة «قراءة جزئية» عندما لا تغطي القراءات اليوم كاملاً.
                </FieldHelp>
            </span>
            <span className="messaging-health__value">
                {value === null || value === undefined ? (
                    <span className="messaging-health__muted">غير متاح</span>
                ) : (
                    <>
                        <Ltr>{value}</Ltr>
                        <span className="messaging-health__cities">
                            {isProviderObserved ? " رصيد" : " رسالة مسجّلة"}
                        </span>
                        {isProviderObserved && coverage !== "complete" ? (
                            <span className="messaging-health__cities"> — قراءة جزئية</span>
                        ) : null}
                    </>
                )}
                {failed ? (
                    <span className="messaging-health__cities">
                        {" — فشل مسجّل "}
                        <Ltr>{failed}</Ltr>
                    </span>
                ) : null}
            </span>
        </div>
    );
};

const InstanceRow = ({ instance }) => {
    const { label, phone } = describeInstance(instance);
    const meta = WHATSAPP_STATUS_META[instance.status];

    return (
        <div className="messaging-health__row">
            <span className="messaging-health__name messaging-health__name--stacked">
                <span className="messaging-health__instance">
                    {label}
                    {phone && (
                        <>
                            {" — "}
                            <Ltr>{phone}</Ltr>
                        </>
                    )}
                </span>
                {/* Which cities go out through this instance. Plainly listed
                    rather than hidden behind a tooltip: when one is down, "who
                    is affected" is the first thing asked, not the last. */}
                {instance.cities?.length ? (
                    <span className="messaging-health__cities">{instance.cities.join("، ")}</span>
                ) : null}
                {/* Lifetime counters from UltraMsg. `expired` is the one that
                    matters: messages queued until the provider gave up, i.e.
                    sent as far as the platform knew and received by nobody. */}
                {/* Today and yesterday, from differencing the lifetime
                    counters. Yesterday is what makes today legible — 40 expired
                    means nothing until you know yesterday was 2 or 400. Each is
                    absent until there are two readings to subtract, rather than
                    showing a zero it cannot stand behind. */}
                <DayCounters label="اليوم" counts={instance.today} />
                <DayCounters label="أمس" counts={instance.yesterday} />
                {instance.stats ? (
                    <span className="messaging-health__stats">
                        الإجمالي: أُرسلت <Ltr>{instance.stats.sent}</Ltr>
                        {" · "}
                        بالانتظار <Ltr>{instance.stats.queue}</Ltr>
                        {" · "}
                        <span className={instance.stats.expired > 0 ? "is-bad" : undefined}>
                            لم تصل <Ltr>{instance.stats.expired}</Ltr>
                        </span>
                        {" · "}
                        أرقام بلا واتساب <Ltr>{instance.stats.invalid}</Ltr>
                    </span>
                ) : null}
            </span>
            <span className="messaging-health__status">
                <StatusPill isHealthy={instance.isHealthy}>
                    {meta?.label || instance.status || "غير معروف"}
                </StatusPill>
                <FieldHelp>
                    {meta?.help ||
                        `حالة غير معروفة ("${instance.status ?? "?"}") — راجع لوحة UltraMsg.`}
                </FieldHelp>
            </span>
        </div>
    );
};

const MessagingHealth = () => {
    const { health, loading, refreshing, error, refresh } = useGetMessagingHealth();

    if (loading) {
        return (
            <div className="messaging-health">
                <div className="messaging-health__card">
                    <div className="messaging-health__head">
                        <h3>حالة الرسائل</h3>
                    </div>
                    <div className="messaging-health__loading">
                        <Ring size={30} lineWeight={5} speed={2} color={BRAND} />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !health) {
        return (
            <div className="messaging-health">
                <div className="messaging-health__card">
                    <div className="messaging-health__head">
                        <h3>حالة الرسائل</h3>
                    </div>
                    <p className="messaging-health__error">تعذّر قراءة حالة الرسائل.</p>
                </div>
            </div>
        );
    }

    const { whatsapp = [], sms = {} } = health;
    const smsToday = sms.today ?? {
        consumed: null,
        coverage: null,
        sent: sms.sentToday,
        failed: sms.failedToday,
    };
    const smsYesterday = sms.yesterday ?? {};

    return (
        <div className="messaging-health">
            <div className="messaging-health__card">
                <div className="messaging-health__head">
                    <h3>حالة الرسائل</h3>
                    <button
                        type="button"
                        className="messaging-health__refresh"
                        onClick={() => refresh()}
                        disabled={refreshing}
                    >
                        {refreshing ? "جارٍ التحديث..." : "تحديث"}
                    </button>
                </div>

                <div className="messaging-health__banner">
                    <StatusPill isHealthy={health.isHealthy}>
                        {health.isHealthy
                            ? "الرسائل تعمل بشكل طبيعي"
                            : "هناك مشكلة في إرسال الرسائل"}
                    </StatusPill>
                    <span className="messaging-health__age">
                        آخر فحص: {formatAge(health.observedAt)}
                    </span>
                </div>

                {/* WhatsApp — one row per instance. */}
                <div className="messaging-health__section">
                    <h4>واتساب</h4>
                    {whatsapp.map((instance) => (
                        <InstanceRow key={instance.instanceId} instance={instance} />
                    ))}

                    {whatsapp.some((instance) => !instance.isHealthy) && (
                        <p className="messaging-health__hint">
                            الرسائل يتم تخزينها في قائمة انتظار ولا تصل للزبائن. افتح لوحة UltraMsg
                            وأعد ربط الرقم.
                            <strong> امسح قائمة الانتظار (Clear the queue) قبل مسح رمز QR</strong> —
                            وإلا سيتم إرسال كل الرسائل القديمة دفعة واحدة (طلبات قديمة للمطاعم
                            والزبائن).
                        </p>
                    )}
                </div>

                {/* SMS — off is a decision, not a fault, so it reads neutrally. */}
                <div className="messaging-health__section">
                    <h4>الرسائل النصية (SMS)</h4>
                    {!sms.isEnabled ? (
                        <div className="messaging-health__row">
                            <span className="messaging-health__name">الحالة</span>
                            <span className="messaging-health__pill messaging-health__pill--off">
                                متوقفة
                            </span>
                        </div>
                    ) : (
                        <>
                            <div className="messaging-health__row">
                                <span className="messaging-health__name">
                                    الرصيد
                                    <FieldHelp>
                                        الرصيد المتبقي لدى مزوّد الرسائل النصية. الرسالة المرفوضة
                                        تستهلك رصيداً أيضاً، لذا قد ينخفض أسرع من عدد الرسائل
                                        الواصلة.
                                    </FieldHelp>
                                </span>
                                <StatusPill isHealthy={sms.balance !== null}>
                                    {sms.balance === null ? (
                                        "تعذّر قراءة الرصيد"
                                    ) : (
                                        <>
                                            <Ltr>{sms.balance}</Ltr> رسالة
                                        </>
                                    )}
                                </StatusPill>
                            </div>
                            <SmsDayRow label="اليوم" day={smsToday} />
                            <SmsDayRow label="أمس" day={smsYesterday} />
                            {sms.daysOfCredit !== null && sms.daysOfCredit !== undefined ? (
                                <div className="messaging-health__row">
                                    <span className="messaging-health__name">
                                        الرصيد يكفي
                                        <FieldHelp>
                                            تقدير بناءً على معدل الإرسال في آخر ٧ أيام. يظهر فقط عند
                                            وجود إرسال كافٍ للتقدير.
                                        </FieldHelp>
                                    </span>
                                    <span className="messaging-health__value">
                                        <Ltr>{sms.daysOfCredit}</Ltr> يوم
                                    </span>
                                </div>
                            ) : null}
                            <div className="messaging-health__row">
                                <span className="messaging-health__name">
                                    الحد اليومي
                                    <FieldHelp>
                                        أقصى عدد رسائل نصية يرسلها النظام في اليوم الواحد. حدٌّ
                                        وقائي — عند بلوغه تتوقف الرسائل النصية ويبقى الواتساب يعمل.
                                    </FieldHelp>
                                </span>
                                <span className="messaging-health__value">
                                    <Ltr>{sms.dailyCap}</Ltr>
                                </span>
                            </div>
                            <div className="messaging-health__row">
                                <span className="messaging-health__name">
                                    الدول المسموح بها
                                    <FieldHelp>
                                        مفاتيح الدول التي يُسمح بإرسال رسائل نصية إليها. أي رقم
                                        خارجها يُرفض قبل الوصول للمزوّد، حمايةً من استنزاف الرصيد.
                                    </FieldHelp>
                                </span>
                                <span className="messaging-health__value">
                                    <Ltr>{(sms.allowedCountryCodes || []).join(", ")}</Ltr>
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* Push has no "disconnected" state — if Firebase were broken
                    the API would not be running. Its silent failure is an
                    account with no device token, which nothing else reports. */}
                {health.push?.length ? (
                    <div className="messaging-health__section">
                        <h4>إشعارات التطبيق</h4>
                        {health.push.map((entry) => (
                            <PushRow key={entry.role} entry={entry} />
                        ))}
                    </div>
                ) : null}

                {/* What actually goes out, and on which channel. Answers the
                    question the two sections above provoke: "fine — but which
                    of our messages does this affect?" */}
                <div className="messaging-health__section">
                    <h4>ما الذي يُرسل</h4>
                    {(health.messageTypes ?? []).map((type) => (
                        <MessageTypeRow key={type.key} type={type} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MessagingHealth;
